/** MVT / GeoJSON property keys that may hold an entity id. */
const ENTITY_ID_KEYS = [
	"ID",
	"id",
	"Id",
	"AssetId",
	"assetId",
	"asset_id",
	"JobId",
	"jobId",
	"job_id",
	"TicketId",
	"ticketId",
	"ticket_id",
	"RecordId",
	"recordId",
	"record_id",
] as const;

const UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface MapFeatureLike {
	id?: string | number | undefined;
	properties?: Record<string, unknown> | null;
}

function normalizeEntityId(value: unknown): string | null {
	if (typeof value !== "string" || value.length === 0) return null;
	return UUID_PATTERN.test(value) ? value : null;
}

/**
 * Reads the entity id from map feature properties using common MVT key variants.
 */
export function getEntityIdFromFeature(
	properties: Record<string, unknown> | null | undefined,
): string | null {
	const props = properties ?? {};

	for (const key of ENTITY_ID_KEYS) {
		const id = normalizeEntityId(props[key]);
		if (id) return id;
	}

	return null;
}

/**
 * Reads the entity id from a Mapbox rendered feature (properties, then top-level id).
 * Mapbox MVT features often expose a numeric tile `feature.id` — that must not be
 * used as an entity id; the API GUID lives in properties (`ID`, `JobId`, etc.).
 */
export function getEntityIdFromMapFeature(
	feature: MapFeatureLike | null | undefined,
): string | null {
	if (!feature) return null;

	const fromProps = getEntityIdFromFeature(feature.properties);
	if (fromProps) return fromProps;

	return normalizeEntityId(feature.id);
}
